import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { environment } from '@environments/environment';
import { CurrentTenantService, ITenant, TenantService } from '@suvo-bi-users';
import { Subscription } from 'rxjs';

interface IShop extends ITenant {
  role: string;
}

@Component({
  standalone: true,
  imports: [MatIconModule, RouterLink],
  template: `
    <div class="component-container">
      @if (!isLoading) {
        <header class="top-bar">
          <div class="logout-btn" (click)="onLogout()">
            <mat-icon>logout</mat-icon>
            <span>Log Out</span>
          </div>
        </header>
      }

      @if (isInitialised) {
        <div class="content-shell">
          <div class="intro">
            <h1>Welcome</h1>
            <p>Which shop would you like to manage?</p>
          </div>

          <div class="shop-list">
            @for (shop of shops; track $index) {
              <div class="shop card" (click)="onShopSelect(shop.firebaseTenantId)">
                <div class="card-heading">
                  <mat-icon>store</mat-icon>
                  <h2>{{ shop.tenantName }}</h2>
                </div>
                <small>{{ shop.role }}</small>
              </div>
            }
            <div class="new-shop card" routerLink="/new-shop">
              <div class="centered">
                <mat-icon>add_circle_outline</mat-icon>
                <p>Create a new shop</p>
              </div>
            </div>
          </div>
        </div>
      }

      @if (isLoading) {
        <div class="loading-state">
          <div class="loading-graphic" role="status" aria-live="polite" aria-label="Loading">
            <div class="loading-track">
              <div class="loading-bar"></div>
            </div>
            <p>Preparing your admin workspace</p>
          </div>
        </div>
      }
    </div>
  `,
  styles: `
    .component-container {
      min-height: calc(100vh);
      background: #f6f7fb;
      color: #1f2a44;
      padding: 20px;
    }

    .top-bar {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 18px;
    }

    .content-shell {
      max-width: 1080px;
      margin: 0 auto;
    }

    .intro {
      text-align: center;
      margin-bottom: 6px;
    }

    .intro h1 {
      margin: 0;
      font-size: clamp(2rem, 2.6vw, 2.6rem);
      font-weight: 650;
      line-height: 1.12;
      letter-spacing: -0.01em;
      color: #1f2a44;
    }

    .intro p {
      margin: 14px auto 0;
      max-width: 540px;
      font-size: clamp(1rem, 1.2vw, 1.125rem);
      line-height: 1.5;
      color: #5c6478;
    }

    .logout-btn {
      border: 1px solid #d8deeb;
      border-radius: 10px;
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 12px;
      transition: 120ms ease-out;
      cursor: pointer;
      color: #2f3c5b;

      /* Put it on top of "loading" overlay, allowing logout if something is broken */
      background: #ffffff;
      z-index: 10;

      &:hover {
        background: #f4f6fb;
        border-color: #c8d0e2;
      }
    }

    .shop-list {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
      margin: 32px 0 0;
      gap: 16px;
      text-align: left;
    }

    .shop {
      display: flex;
      justify-content: space-between;
      cursor: pointer;
      padding: 18px;
      min-height: 132px;
      flex-flow: column nowrap;
      color: #1f2a44;
      border: 1px solid #e3e8f2;
      border-radius: 12px;
      background: #ffffff;
      transition:
        border-color 120ms ease-out,
        box-shadow 120ms ease-out,
        transform 120ms ease-out;

      &:hover {
        border-color: #c7d1e5;
        box-shadow: 0 8px 18px rgba(17, 24, 39, 0.08);
        transform: translateY(-1px);
      }

      h2 {
        margin: 0;
        font-size: 1.1rem;
        line-height: 1.3;
      }

      small {
        color: #5c6478;
        font-size: 0.88rem;
      }
    }

    .centered {
      margin: auto;
      text-align: center;
    }

    .card-heading {
      display: flex;
      gap: 8px;

      mat-icon {
        margin-top: 4px;
        color: #445275;
      }
    }

    .new-shop {
      cursor: pointer;
      padding: 18px;
      min-height: 132px;
      display: flex;
      justify-content: center;
      background: #fbfcff;
      border: 1px dashed #b9c5df;
      border-radius: 12px;
      color: #2f3c5b;
      transition:
        border-color 120ms ease-out,
        background-color 120ms ease-out,
        transform 120ms ease-out;

      &:hover {
        border-color: #95a7cc;
        background: #f5f8ff;
        transform: translateY(-1px);
      }

      mat-icon {
        font-size: 36px;
        height: 36px;
        width: 36px;
        color: #586b95;
      }

      p {
        margin: 6px 0 0;
        font-size: 1.02rem;
        color: #2f3c5b;
      }
    }

    .loading-state {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: calc(100vh - 40px);
      animation: fadeIn 140ms ease-out;
    }

    .loading-graphic {
      width: min(360px, calc(100vw - 40px));
      text-align: center;
    }

    .loading-track {
      height: 6px;
      width: 100%;
      border-radius: 999px;
      background: #e5eaf5;
      overflow: hidden;
      position: relative;
    }

    .loading-bar {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 42%;
      border-radius: 999px;
      background: linear-gradient(90deg, #b7c3de 0%, #6f83b3 100%);
      animation: loadingSlide 1.35s ease-in-out infinite;
    }

    .loading-graphic p {
      margin: 12px 0 0;
      font-size: 0.95rem;
      letter-spacing: 0.01em;
      color: #637190;
    }

    @media (max-width: 768px) {
      .component-container {
        padding: 14px;
      }

      .intro h1 {
        line-height: 1.16;
      }

      .intro p {
        margin-top: 12px;
      }

      .shop-list {
        grid-template-columns: 1fr;
      }
    }

    @keyframes fadeIn {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }

    @keyframes loadingSlide {
      0% {
        transform: translateX(-115%);
      }
      100% {
        transform: translateX(260%);
      }
    }
  `,
})
export class ShopSelectScreenComponent implements OnInit, OnDestroy {
  private subscription!: Subscription;
  shops = new Array<IShop>();
  isInitialised = false;
  isLoading = false;
  private readonly lastTenantStorageKey = 'lastSelectedTenantId';

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly angularFireAuth: AngularFireAuth,
    private readonly currentTenantService: CurrentTenantService,
    private readonly router: Router,
    private readonly tenantService: TenantService,
  ) {}

  ngOnInit() {
    this.subscription = this.angularFireAuth.user.subscribe(async (user) => {
      if (user) {
        this.isLoading = true;
        this.shops = [];
        const tokenResult = await user.getIdTokenResult(true);
        const tenantRoles: Record<string, string> = tokenResult.claims.context?.tenantRoles;
        if (!tenantRoles) {
          return this.onLogout();
        } else if (!Object.values(tenantRoles).length) {
          return this.router.navigate(['/new-shop']);
        }

        const tenants = await this.tenantService.getMyTenants();
        const autoRedirect = this.activatedRoute?.snapshot?.queryParams['autoRedirect'] === 'true';
        const redirectUrl = this.activatedRoute?.snapshot?.queryParams['redirectUrl'];
        const lastSelectedTenantId = localStorage.getItem(this.lastTenantStorageKey);
        if (redirectUrl && lastSelectedTenantId) {
          const lastTenant = tenants.find(
            (tenant) => tenant.firebaseTenantId === lastSelectedTenantId && tenantRoles[tenant._id],
          );
          if (lastTenant) {
            return this.onShopSelect(lastTenant.firebaseTenantId);
          }
        }
        if (tenants.length == 1 && (autoRedirect || !!redirectUrl)) {
          // Auto-select the only tenant only when explicitly requested.
          return this.onShopSelect(tenants[0].firebaseTenantId);
        }
        if (tenants) {
          this.shops = tenants
            .filter((tenant) => tenantRoles[tenant._id])
            .map((tenant) => ({
              ...tenant,
              role: tenantRoles[tenant._id],
            }));
        }
        this.isLoading = false;
        this.isInitialised = true;
      }
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async onShopSelect(firebaseTenantID: string) {
    this.isLoading = true;

    localStorage.setItem(this.lastTenantStorageKey, firebaseTenantID);

    await this.currentTenantService.init(
      `${environment.usersServiceApi}tenant/${firebaseTenantID}`,
    );

    const redirectUrl = this.activatedRoute?.snapshot?.queryParams['redirectUrl'];

    if (redirectUrl) {
      await this.router.navigateByUrl(decodeURIComponent(redirectUrl));
    } else {
      await this.router.navigate(['/', 'private', 'overview']);
    }
  }

  async onLogout() {
    await this.angularFireAuth.signOut();
    await this.router.navigate(['/', 'login']);
  }
}
